import { State, process } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { RadioGroup, RadioGroupChangeEvent } from '@progress/kendo-react-inputs';
import { useCallback, useMemo, useState } from 'react';
import uuid from 'react-uuid';
import useCheckbookConfigurationColumns from 'src/assets/static/checkbook_configuration/useCheckbookConfigurationColumns';
import DarkBackgroundLoadingPanel from 'src/components/DarkBackgroundLoadingPanel';
import GridWrapper from 'src/components/GridWrapper';
import LoadingPanel from 'src/components/LoadingPanel';
import useCheckbookConfiguration from 'src/hooks/useCheckbookConfiguration';
import useConfirm from 'src/hooks/useConfirm';
import useWindowSize from 'src/hooks/useWindowSize';
import { GlCodeFormatType } from 'src/services/useGetGlMasks';
import CheckbookConfigurationTableToolbar from './CheckbookConfigurationTableToolbar';
import { GLCodeGuideMeSteps } from './GuideMeContent';

const selectable: GridSelectableSettings = {
  enabled: false,
  drag: false,
  cell: false,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 2,
  pageSizes: [5, 10, 15],
};

const defaultProps = {
  tableTextId: 'Formats',
  DataItemKey: 'rowId',
  sortable: false,
  resizable: false,
  showColumnMenu: false,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const initialState: State = {
  skip: 0,
  take: 5,
  sort: [],
  filter: {
    logic: 'and',
    filters: [],
  },
};
const glCodeFormatRadioData = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const CheckbookConfigurationTable = () => {
  const { confirm } = useConfirm();
  const { checkbookConfigurationColumns } = useCheckbookConfigurationColumns();
  const {
    t,
    setGlCodeFormats,
    areFormatsRequired,
    setAreFormatsRequired,
    editingGLCodeFormatDataWithErrors,
    isGettingInvalidGLCodes,
    isPageEditable,
    areGLMasksLoading,
    isManagementCompany,
  } = useCheckbookConfiguration();
  const guideMe = GLCodeGuideMeSteps(false)[0];
  const [state, setState] = useState(initialState);

  const { data, total } = useMemo(
    () =>
      process(editingGLCodeFormatDataWithErrors, state) as {
        data: GlCodeFormatType[];
        total: number;
      },
    [editingGLCodeFormatDataWithErrors, state]
  );

  const selectedRows = useMemo(() => data.filter((glCode) => glCode.selected), [data]);

  const onCheckbookSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setGlCodeFormats((prev) => {
        const prevItems = [...prev].map((item) =>
          item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
        );
        return prevItems;
      });
    },
    [setGlCodeFormats]
  );

  const changeDataState = useCallback(
    (e: GridDataStateChangeEvent) => {
      setState(e.dataState);
      setGlCodeFormats((prev) => [...prev].map((item) => ({ ...item, selected: false })));
    },
    [setGlCodeFormats]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;
      setGlCodeFormats((prev) => {
        const selectedData = data.map((item) => item.rowId);
        return prev?.map((row) => ({
          ...row,
          selected: selectedData.includes(row.rowId) ? checked : row.selected,
        }));
      });
    },
    [data, setGlCodeFormats]
  );

  const handleClearSelection = useCallback(() => {
    setGlCodeFormats((prev) => prev?.map((row) => ({ ...row, selected: false })));
  }, [setGlCodeFormats]);

  const handleAddFormat = useCallback(() => {
    setState((pre) => ({ ...pre, skip: 0 }));
    const newGlCodeFormat: GlCodeFormatType = {
      rowId: uuid(),
      format: '',
      selected: false,
      isNewFormat: true,
    };
    setGlCodeFormats((prev) => [newGlCodeFormat, ...prev]);
  }, [setGlCodeFormats]);

  const handleDelete = useCallback(() => {
    confirm({
      title: 'Delete Selected Format(s)',
      content: 'The selected format(s) will be deleted.',
      confirmButtonText: 'OK',
    }).then((accepted) => {
      if (accepted) {
        const deletedFormatIds = selectedRows.map((row) => row.rowId);
        setGlCodeFormats((prev) => prev.filter((item) => !deletedFormatIds.includes(item.rowId)));
        if (state.skip && state.take && selectedRows.length === total - state.skip) {
          setState({
            ...state,
            skip: state.skip - state.take,
          });
        }
      }
    });
  }, [confirm, selectedRows, setGlCodeFormats, state, total]);

  const handleItemChange = useCallback(
    (e: GridItemChangeEvent) => {
      const { rowId } = e.dataItem;
      const { field = '', value } = e;
      setGlCodeFormats((prev) =>
        [...prev].map((item) => ({
          ...item,
          ...(rowId === item.rowId && {
            [field]: value,
          }),
        }))
      );
    },
    [setGlCodeFormats]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <CheckbookConfigurationTableToolbar
        handleClearSelection={handleClearSelection}
        selectedRows={selectedRows}
        handleAddFormat={handleAddFormat}
        handleDelete={handleDelete}
      />
    ),
    [handleAddFormat, handleClearSelection, handleDelete, selectedRows]
  );

  const handleGlCodeFormatRequireChange = (event: RadioGroupChangeEvent) => {
    const { value } = event;
    if (value === 'yes') {
      setAreFormatsRequired(true);
    } else {
      setAreFormatsRequired(false);
    }
  };

  const editAdminText = (
    <div>
      As an administrator at a top-level company, you have the option to create a required GL Code
      format(s) for subordinate companies to follow.
      <br />
      By default, all subordinate companies will be required to utilize GL Codes in these formats.
    </div>
  );

  const highlightedText = isPageEditable ? (
    editAdminText
  ) : (
    <>
      Contact an administrator at a top-level company to create and edit required GL Code format(s).
      <br />
      By default, all subordinate companies will be required to match the format(s) set here when
      creating/updating GL Codes.
    </>
  );

  const companyHighlightedText = isPageEditable ? (
    editAdminText
  ) : (
    <>
      Contact iBuyEfficient customer support to enable GL code format administration for this
      account.
      <br />
      By default, all subordinate companies will be required to match the format(s) set here when
      creating/updating GL Codes.
    </>
  );

  const { height } = useWindowSize();

  return (
    <>
      {isGettingInvalidGLCodes && (
        <DarkBackgroundLoadingPanel
          id="checkbookConfigurationTable"
          loadingText="Analyzing existing codes and formats..."
        />
      )}
      {areGLMasksLoading && <LoadingPanel />}
      <div className="flex gap-6">
        <div className="w-[55%]">
          <p className="p-3 inline-block rounded bg-[#3FA9DB33] text-sm font-medium">
            {isManagementCompany ? companyHighlightedText : highlightedText}
          </p>
          {isPageEditable ? (
            <div className="mt-5 mb-5">
              <label className="font-semibold block mb-2">
                Require GL Code formats for subordinate companies?
              </label>
              <RadioGroup
                data={glCodeFormatRadioData}
                layout={'horizontal'}
                className="text-xs"
                onChange={handleGlCodeFormatRequireChange}
                value={
                  areFormatsRequired === true
                    ? 'yes'
                    : areFormatsRequired === false
                    ? 'no'
                    : undefined
                }
              />
            </div>
          ) : (
            <p className="font-bold text-sm my-6">
              {areFormatsRequired && 'The below GL Code Formats are required.'}
            </p>
          )}

          {areFormatsRequired ? (
            <GridWrapper
              id="checkbookConfigurationTable"
              style={{ maxHeight: `${height - 240}px` }}
              columns={checkbookConfigurationColumns}
              dataSource={data}
              dataState={state}
              total={total}
              gridToolbarChildren={isPageEditable ? gridToolbarChildren : undefined}
              onDataStateChange={changeDataState}
              {...defaultProps}
              onHeaderSelectionChange={onHeaderSelectionChange}
              onSelectionChange={onCheckbookSelectionChange}
              noRecordText={t('No required formats set')}
              onItemChange={handleItemChange}
            />
          ) : (
            <p className="font-bold text-sm my-6">No formats required.</p>
          )}
        </div>
        {areFormatsRequired ? (
          <div className="w-[45%]">
            <div className="bg-info bg-opacity-[10%] p-6 rounded-md border border-[#0E80B4]">
              <p className="mb-4">{guideMe.title}</p>
              {guideMe.content}
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    </>
  );
};

export default CheckbookConfigurationTable;
